/* eslint-disable react/require-default-props */
import React, { useContext, useMemo, useEffect } from 'react';
import {
  makeStyles,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuButton,
  mergeClasses,
  Overflow,
  OverflowItem,
  useIsOverflowItemVisible,
  useOverflowMenu,
} from '@fluentui/react-components';

import { MoreHorizontalFilled } from '@fluentui/react-icons';
import { LayoutSize } from '@bingads-webui-fluent-component/layout-size';

import { Spacer } from './spacer';
import { MenuContext } from './menu-context';

const useStyles = makeStyles({
  container: {
    height: '60px',
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    minWidth: 0,
  },
  overFlowContainer: {
    height: '60px',
    display: 'flex',
    flexWrap: 'nowrap',
    flexGrow: '1',
    alignItems: 'center',
    minWidth: 0,
  },
  overFlowMenuButton: {
    minWidth: '48px',
    height: '48px',
  },
});

const OverflowMenuItem: React.FC<{
  id: string;
  item: any;
}> = ({ id, item }) => {
  const isVisible = useIsOverflowItemVisible(id);

  if (isVisible) {
    return null;
  }

  return React.cloneElement(item, {
    as: MenuItem,
    key: item.props.itemKey,
  });
};

const OverflowMenu: React.FC<{
  items: any[];
}> = ({ items }) => {
  const { ref, isOverflowing } = useOverflowMenu<HTMLButtonElement>();
  const classes = useStyles();

  if (!isOverflowing) {
    return null;
  }

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <MenuButton className={mergeClasses(classes.overFlowMenuButton, 'overflow-menu-button')} ref={ref} icon={<MoreHorizontalFilled />} appearance="subtle" />
      </MenuTrigger>

      <MenuPopover>
        <MenuList className="overflow-menu">
          {items.map((item) => (
            <OverflowMenuItem key={item.props.itemKey} id={item.props.itemKey} item={item} />
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export const DefaultTemplate: React.FC<{
  items: any[];
  templateClassName?: string;
}> = ({ items, templateClassName }) => {
  const classes = useStyles();
  const {
    layoutSize,
    singleComponentModeKey,
    dynamicallyPinedItemKey,
  } = useContext(MenuContext);
  const [singleComponentModeIndex, setSingleComponentModeIndex] = React.useState(-1);

  const navItems = [...items];
  // filter out leading pined items so that we can optionally show them when we hide other items
  const leadingNavItems = useMemo(() => {
    const pinnedItems: Array<{ props: { itemKey: string, isPined: boolean } }> = [];
    while (navItems.length > 0 && navItems[0].props.isPined) {
      pinnedItems.push(navItems.shift());
    }
    return pinnedItems as React.ReactElement[];
  }, [navItems]);

  // filter out trailing pined items so that overflow menu can be shown before them
  const trailingNavItems = useMemo(() => {
    const pinnedItems: Array<{ props: { itemKey: string, isPined: boolean } }> = [];
    while (navItems.length > 0 && navItems[navItems.length - 1].props.isPined) {
      pinnedItems.unshift(navItems.pop());
    }
    return pinnedItems as React.ReactElement[];
  }, [navItems]);

  useEffect(() => {
    if (singleComponentModeKey) {
      const index = navItems.findIndex((item) => item.props.itemKey === singleComponentModeKey);
      setSingleComponentModeIndex(index);
    } else {
      setSingleComponentModeIndex(-1);
    }
  }, [navItems, singleComponentModeKey]);

  const pinedCount = React.useMemo(() => {
    const filteredItems = navItems.filter((item) => item && item.props.isPined);
    return filteredItems.length;
  }, [navItems]);

  const renderContent = () => {
    if (singleComponentModeIndex > -1) {
      if (layoutSize === LayoutSize.M) {
        return (
          <>
            {leadingNavItems.map((item) => React.cloneElement(item, { as: MenuButton }))}
            { React.cloneElement(navItems[singleComponentModeIndex], { as: MenuButton })}
          </>
        );
      }

      if (layoutSize === LayoutSize.S) {
        return (
          <>
            { React.cloneElement(navItems[singleComponentModeIndex], { as: MenuButton })}
          </>
        );
      }
    }

    return (
      <>
        {leadingNavItems.map((item) => React.cloneElement(item, { as: MenuButton }))}
        <Overflow minimumVisible={pinedCount}>
          <div className={classes.overFlowContainer}>
            {navItems.map((item) => (
              <OverflowItem
                id={item.props.itemKey}
                key={item.props.itemKey}
                priority={item.props.isPined
                  || (dynamicallyPinedItemKey && dynamicallyPinedItemKey === item.props.itemKey)
                  || (item.type === Spacer)
                  ? 1000 : undefined}
              >
                { React.cloneElement(item, { as: MenuButton })}
              </OverflowItem>
            ))}
            <OverflowMenu items={navItems} />
          </div>
        </Overflow>
        {trailingNavItems.map((item) => React.cloneElement(item, { as: MenuButton }))}
      </>
    );
  };

  return (
    <div className={mergeClasses(classes.container, templateClassName)}>
      {renderContent()}
    </div>
  );
};
